// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// Standard page building blocks (STANDARD.md). Use these, or the same fl-
// classes, so every Flameco page reads like Flameco World's home.
import React from 'react';
import { FlamecoLinkProps, FlamecoMark, Plate, categoryAttrs } from './FlamecoFrame';
import { IconHistory, IconInbox, IconStar } from './icons';

const defaultLink = ({ href, className, children, ...rest }: FlamecoLinkProps) => (
  <a href={href} className={className} {...rest}>
    {children}
  </a>
);

/** Page body width + padding. Put one directly inside the frame. */
export function Content({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`fl-content${className ? ` ${className}` : ''}`}>{children}</div>;
}

/**
 * Mono eyebrow (breadcrumbs), display title, one-line description, actions on
 * the right (primary action last).
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  renderLink = defaultLink,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  breadcrumbs?: { label: string; href?: string }[];
  actions?: React.ReactNode;
  renderLink?: (p: FlamecoLinkProps) => React.ReactNode;
}) {
  return (
    <div className="fl-page-header">
      <div className="fl-page-header__text">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <ol className="fl-crumbs">
            {breadcrumbs.map((b, i) => (
              <li key={i}>{b.href ? renderLink({ href: b.href, className: '', children: b.label }) : b.label}</li>
            ))}
          </ol>
        )}
        <h1 className="fl-page-title">{title}</h1>
        {description && <p className="fl-page-desc">{description}</p>}
      </div>
      {actions && <div className="fl-page-actions">{actions}</div>}
    </div>
  );
}

/**
 * A titled group on a page: title, mono meta beside it, optional actions on the
 * right. `categoryHue` makes it a category: bold title and strip in that colour
 * (null = the neutral graphite category). `count` shows beside the title.
 */
export function Section({
  title,
  description,
  actions,
  categoryHue,
  count,
  children,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  categoryHue?: number | null;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="fl-section" {...categoryAttrs(categoryHue)}>
      {(title || actions) && (
        <div className="fl-section__head">
          {title && <h2 className="fl-section__title">{title}</h2>}
          {count != null && <span className="fl-section__count">{count}</span>}
          {description && <p className="fl-section__desc">{description}</p>}
          {actions && <div className="fl-section__actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** World's joined grid of tiles. Always ToolTiles inside. */
/**
 * The joined tile grid. By default columns auto-fill at a 236px minimum;
 * `columns` fixes the count on wide windows (it steps down on narrower ones).
 */
export function ToolGrid({ children, columns }: { children: React.ReactNode; columns?: number }) {
  const style = columns ? ({ '--fl-tiles-cols': columns } as React.CSSProperties) : undefined;
  return (
    <div className={`fl-tiles${columns ? ' fl-tiles--fixed' : ''}`} style={style}>
      {children}
    </div>
  );
}

/**
 * How every Flameco app shows a tool, module or app, exactly like World's home
 * tiles: a hue-tinted icon plate (top), name and one line (bottom), an arrow
 * on hover. `hue` is an OKLCH hue (0-360); leave it out for a neutral plate.
 * `categoryHue` adds the category's bold strip on the left (null = neutral);
 * `favourite` adds a star that keeps it in Favorites.
 */
export function ToolTile({
  icon,
  hue,
  categoryHue,
  favourite,
  title,
  description,
  href,
  onClick,
  badge,
  meta,
  disabled,
  renderLink = defaultLink,
}: {
  icon?: React.ReactNode;
  hue?: number | null;
  categoryHue?: number | null;
  favourite?: { on: boolean; onToggle: () => void; label?: string };
  title: React.ReactNode;
  description?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  badge?: React.ReactNode;
  meta?: React.ReactNode;
  disabled?: boolean;
  renderLink?: (p: FlamecoLinkProps) => React.ReactNode;
}) {
  const body = (
    <>
      <span className="fl-tile__top">
        {icon ? <Plate hue={hue}>{icon}</Plate> : <span />}
        {badge && <span className="fl-tile__badge">{badge}</span>}
      </span>
      <span className="fl-tile__bottom">
        <span className="fl-tile__title">{title}</span>
        {description && <span className="fl-tile__desc">{description}</span>}
        {meta && <span className="fl-tile__meta">{meta}</span>}
      </span>
    </>
  );
  const cat = categoryAttrs(categoryHue);
  const tile =
    href && !disabled ? (
      <CatLink renderLink={renderLink} href={href} onClick={onClick} className="fl-tile" cat={cat}>
        {body}
      </CatLink>
    ) : (
      <button type="button" className="fl-tile" onClick={disabled ? undefined : onClick} aria-disabled={disabled || undefined} {...cat}>
        {body}
      </button>
    );
  if (!favourite) return tile;
  const name = favourite.label ?? (typeof title === 'string' ? title : 'this module');
  return (
    <div className="fl-tile-cell">
      {tile}
      <button
        type="button"
        className="fl-tile__fav"
        aria-pressed={favourite.on}
        aria-label={favourite.on ? `Remove ${name} from favorites` : `Add ${name} to favorites`}
        title={favourite.on ? 'Remove from favorites' : 'Add to favorites'}
        onClick={(e) => {
          e.stopPropagation();
          favourite.onToggle();
        }}
      >
        <IconStar />
      </button>
    </div>
  );
}

/**
 * The app's router link with a category colour. Router links only pass
 * className / children / onClick through, so the colour is set on the rendered
 * element after mount (a hidden marker finds it).
 */
function CatLink({
  renderLink,
  href,
  onClick,
  className,
  cat,
  title,
  children,
}: {
  renderLink: (p: FlamecoLinkProps) => React.ReactNode;
  href: string;
  onClick?: () => void;
  className: string;
  cat: ReturnType<typeof categoryAttrs>;
  title?: string;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const flag = cat['data-fl-cat'];
  const hue = (cat.style as Record<string, unknown> | undefined)?.['--fl-cat-hue'];
  React.useLayoutEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    if (title) el.title = title;
    if (flag === undefined) el.removeAttribute('data-fl-cat');
    else el.setAttribute('data-fl-cat', flag);
    if (hue != null) el.style.setProperty('--fl-cat-hue', String(hue));
    else el.style.removeProperty('--fl-cat-hue');
  }, [flag, hue, title]);
  return (
    <>
      {renderLink({
        href,
        className,
        onClick: onClick ? () => onClick() : undefined,
        children: (
          <>
            <span ref={ref} hidden />
            {children}
          </>
        ),
      })}
    </>
  );
}

/** One module in Favorites or Recent. */
export interface QuickModule {
  key: string;
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  /** The tool's own plate hue. */
  hue?: number | null;
  /** The category colour (the strip); null = neutral. */
  categoryHue?: number | null;
  /** Category name, shown under the label in Favorites. */
  category?: string;
}

/** "8 min ago", "2 h ago", "yesterday", "Oct 12". */
export function timeAgo(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86400) return 'yesterday';
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Favorites (+ Recent), on top of every app's home: starred modules, then the
 * last ones opened (most recent first). Feed it from useQuickAccess. Leave
 * `recent` out to show Favorites alone, full width.
 */
export function QuickAccess({
  favourites,
  recent,
  renderLink = defaultLink,
  maxRecent = 3,
  maxFavourites = 3,
}: {
  favourites: QuickModule[];
  recent?: { module: QuickModule; at: number }[];
  renderLink?: (p: FlamecoLinkProps) => React.ReactNode;
  maxRecent?: number;
  /** Favorites shown on the home page (the side nav lists them all). */
  maxFavourites?: number;
}) {
  const chip = (m: QuickModule, meta?: string) => {
    const cat = categoryAttrs(m.categoryHue);
    const inner = (
      <>
        {m.icon && (
          <Plate hue={m.hue} small>
            {m.icon}
          </Plate>
        )}
        <span className="fl-quick__text">
          <span className="fl-quick__name">{m.label}</span>
          {meta && <span className="fl-quick__meta">{meta}</span>}
        </span>
      </>
    );
    if (m.href) {
      return (
        <CatLink key={m.key} renderLink={renderLink} href={m.href} onClick={m.onClick} className="fl-quick__chip" cat={cat} title={m.category}>
          {inner}
        </CatLink>
      );
    }
    return (
      <button key={m.key} type="button" className="fl-quick__chip" onClick={m.onClick} title={m.category} {...cat}>
        {inner}
      </button>
    );
  };
  return (
    <section className={`fl-quick${recent ? '' : ' fl-quick--one'}`} aria-label={recent ? 'Favorites and recent' : 'Favorites'}>
      <div className="fl-quick__box">
        <div className="fl-quick__head">
          <IconStar /> Favorites <span className="fl-quick__hint">Star any module to keep it here</span>
        </div>
        <div className="fl-quick__row">
          {favourites.length ? (
            favourites.slice(0, maxFavourites).map((m) => chip(m, m.category))
          ) : (
            <p className="fl-quick__empty">Nothing starred yet. Hover a module and tap the star.</p>
          )}
        </div>
      </div>
      {recent && (
        <div className="fl-quick__box">
          <div className="fl-quick__head">
            <IconHistory /> Recent <span className="fl-quick__hint">The last modules you opened</span>
          </div>
          <div className="fl-quick__row">
            {recent.length ? (
              recent.slice(0, maxRecent).map((r) => chip(r.module, timeAgo(r.at)))
            ) : (
              <p className="fl-quick__empty">Modules you open show up here.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** In-page tabs (sections of one page), World's pill tabs. Moving between pages belongs in the side nav. */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { key: K; label: React.ReactNode }[];
  value: K;
  onChange: (key: K) => void;
  label?: string;
}) {
  return (
    <div className="fl-tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" className="fl-tab" aria-selected={t.key === value} onClick={() => onChange(t.key)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export type ChipTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/** Status pill (mono caps). Tone by meaning: success = done/ok, warning = needs attention, danger = failed/blocked, info = in progress. */
export function StatusChip({ tone = 'neutral', children }: { tone?: ChipTone; children: React.ReactNode }) {
  return <span className={`fl-chip${tone === 'neutral' ? '' : ` fl-chip--${tone}`}`}>{children}</span>;
}

/** Nothing to show: icon, title, one line, at most one action. */
export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  text?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="fl-empty">
      <span className="fl-empty__icon">{icon ?? <IconInbox />}</span>
      <p className="fl-empty__title">{title}</p>
      {text && <p className="fl-empty__text">{text}</p>}
      {action && <div className="fl-empty__action">{action}</div>}
    </div>
  );
}

/**
 * Whole-screen states outside the frame: "Signing you in…", "Signed out",
 * "Something went wrong" with Try again / Sign out.
 */
export function StateScreen({
  title,
  text,
  busy,
  actions,
}: {
  title: React.ReactNode;
  text?: React.ReactNode;
  busy?: boolean;
  actions?: React.ReactNode;
}) {
  return (
    <div className="fl-center fl-c">
      <div className="fl-center__card" role={busy ? 'status' : undefined}>
        <FlamecoMark className="fl-center__mark" />
        <p className="fl-center__title">{title}</p>
        {text && <p className="fl-center__text">{text}</p>}
        {busy && <span className="fl-spinner" style={{ color: 'var(--fl-accent)' }} />}
        {actions && <div className="fl-center__actions">{actions}</div>}
      </div>
    </div>
  );
}
